import axios from 'axios';
import { InventoryTransaction, TransactionType } from '../types/inventory';

export interface InventoryItem {
  _id: string;
  warehouse?: {
    _id: string;
    name: string;
    location?: string;
  };
  unitName?: string;
  quantity: number;
  reservedQuantity?: number;
  minQuantity: number;
}

// Get all inventory transactions for a product (legacy function name)
export const getProductTransactions = async (
  productId: string,
  variantId?: string,
  page = 1,
  limit = 10
) => {
  try {
    // Fixed API endpoint to match server route
    let url = `/api/inventory-transactions/product/${productId}?page=${page}&limit=${limit}`;
    if (variantId) {
      url += `&variantId=${variantId}`;
    }
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching product inventory transactions:', error);
    throw error;
  }
};

// Get inventory movement summary
export const getInventoryMovementSummary = async (productId: string) => {
  try {
    // Fixed API endpoint to match server route
    const response = await axios.get(`/api/inventory-transactions/summary/product/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory movement summary:', error);
    throw error;
  }
};

// Get all inventory transactions for a product (new function name)
export const getProductInventoryTransactions = async (
  productId: string,
  variantId?: string,
  page = 1,
  limit = 20,
  search?: string,
  dateFrom?: string,
  dateTo?: string,
  warehouseId?: string
) => {
  try {
    let url = '';
    const queryParams: string[] = [];
    
    // إضافة معاملات البحث المشتركة
    queryParams.push(`page=${page}`);
    queryParams.push(`limit=${limit}`);
    
    if (search) queryParams.push(`search=${encodeURIComponent(search)}`);
    if (dateFrom) queryParams.push(`dateFrom=${dateFrom}`);
    if (dateTo) queryParams.push(`dateTo=${dateTo}`);
    if (warehouseId) queryParams.push(`warehouseId=${warehouseId}`);
    
    // إذا كان معرف المنتج "all" فهذا يعني جلب جميع حركات المخزون لكل المنتجات
    if (productId === 'all') {
      url = `/api/inventory-transactions`;
    } else {
      url = `/api/inventory-transactions/product/${productId}`;
      if (variantId) {
        queryParams.push(`variantId=${variantId}`);
      }
    }
    
    // إضافة معاملات البحث إلى عنوان URL
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await axios.get(url, {
      headers: {
        'x-permission-context': 'inventory-movement'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching product inventory transactions:', error);
    
    // بيانات وهمية للاختبار - ستستبدل ببيانات حقيقية من الخادم
    const dummyData = {
      success: true,
      transactions: [
        {
          _id: 'dummy-1',
          type: 'addition',
          warehouse: 'wh-1',
          warehouseName: 'المستودع الرئيسي',
          product: 'p-1',
          productName: 'قميص قطني',
          variantId: 'v-1',
          variantAttributes: { اللون: 'أزرق', المقاس: 'XL', barcode: '1234567890' },
          quantity: 10,
          previousQuantity: 0,
          newQuantity: 10,
          unit: 'u-1',
          unitName: 'قطعة',
          referenceModel: 'Invoice',
          referenceNumber: '12345',
          notes: 'إضافة مخزون جديد',
          transactionDate: new Date().toISOString(),
          createdAt: new Date().toISOString()
        },
        {
          _id: 'dummy-2',
          type: 'subtraction',
          warehouse: 'wh-1',
          warehouseName: 'المستودع الرئيسي',
          product: 'p-1',
          productName: 'قميص قطني',
          variantId: 'v-1',
          variantAttributes: { اللون: 'أزرق', المقاس: 'XL', barcode: '1234567890' },
          quantity: 3,
          previousQuantity: 10,
          newQuantity: 7,
          unit: 'u-1',
          unitName: 'قطعة',
          referenceModel: 'Invoice',
          referenceNumber: '54321',
          notes: 'بيع',
          transactionDate: new Date().toISOString(),
          createdAt: new Date().toISOString()
        }
      ],
      total: 2,
      count: 2,
      totalPages: 1,
      currentPage: 1
    };
    
    return {
      success: false,
      message: 'فشل في جلب بيانات حركة المخزون',
      transactions: [],
      totalPages: 1,
      currentPage: 1
    };
  }
};

// Create a new inventory transaction
export const createInventoryTransaction = async (transactionData: {
  type: TransactionType;
  warehouseId: string;
  productId: string;
  variantId?: string;
  quantity: number;
  unitId: string;
  destinationWarehouseId?: string;
  notes?: string;
}) => {
  try {
    // Fixed API endpoint to match server route
    const response = await axios.post('/api/inventory-transactions', transactionData);
    return response.data;
  } catch (error) {
    console.error('Error creating inventory transaction:', error);
    throw error;
  }
};

// Get inventory for a specific warehouse
export const getWarehouseInventory = async (warehouseId: string) => {
  try {
    // Fixed API endpoint to match server route
    const response = await axios.get(`/api/inventory/warehouse/${warehouseId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching warehouse inventory:', error);
    throw error;
  }
};

// Get product inventory across all warehouses
export const getProductInventory = async (productId: string) => {
  try {
    // Fixed API endpoint to match server route
    const response = await axios.get(`/api/inventory/product/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product inventory:', error);
    throw error;
  }
};

// Get product variant inventory across all warehouses
export const getProductVariantInventory = async (productId: string, variantId: string) => {
  try {
    // Fixed API endpoint to match server route
    const response = await axios.get(`/api/inventory/product/${productId}/variant/${variantId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product variant inventory:', error);
    throw error;
  }
};

// Create a warehouse transfer
export const createWarehouseTransfer = async (transferData: {
  fromWarehouse: { _id: string; name: string };
  toWarehouse: { _id: string; name: string };
  products: Array<{
    productId: string;
    name: string;
    quantity: number;
    unitId: string;
    unitName: string;
    variantId?: string;
  }>;
  date: Date;
  notes?: string;
}) => {
  try {
    const response = await axios.post('/api/warehouse-transfers', transferData);
    return response.data;
  } catch (error) {
    console.error('Error creating warehouse transfer:', error);
    throw error;
  }
};

// Get all warehouse transfers
export const getWarehouseTransfers = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(`/api/warehouse-transfers?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching warehouse transfers:', error);
    throw error;
  }
};

// Get transfer by ID
export const getWarehouseTransferById = async (transferId: string) => {
  try {
    const response = await axios.get(`/api/warehouse-transfers/${transferId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching warehouse transfer:', error);
    throw error;
  }
};

// Execute (finalize) a transfer
export const executeWarehouseTransfer = async (transferId: string) => {
  try {
    const response = await axios.put(`/api/warehouse-transfers/${transferId}/execute`);
    return response.data;
  } catch (error) {
    console.error('Error executing warehouse transfer:', error);
    throw error;
  }
};

// Generate mock inventory data for testing and development
export const getMockInventoryData = (productId: string) => {
  // Mock data that simulates API response
  return {
    success: true,
    inventoryItems: [
      {
        _id: 'mock-inv-1',
        warehouse: {
          _id: 'wh-1',
          name: 'المستودع الرئيسي',
          location: 'الرياض'
        },
        unitName: 'قطعة',
        quantity: 120,
        reservedQuantity: 15,
        minQuantity: 50
      },
      {
        _id: 'mock-inv-2',
        warehouse: {
          _id: 'wh-2',
          name: 'مستودع الفرع',
          location: 'جدة'
        },
        unitName: 'قطعة',
        quantity: 45,
        reservedQuantity: 5,
        minQuantity: 30
      },
      {
        _id: 'mock-inv-3',
        warehouse: {
          _id: 'wh-3',
          name: 'المستودع الشرقي',
          location: 'الدمام'
        },
        unitName: 'قطعة',
        quantity: 18,
        reservedQuantity: 0,
        minQuantity: 25
      }
    ]
  };
};

// Get all inventory movements
export const getInventoryMovements = async (
  params?: {
    productId?: string;
    variantId?: string;
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }
) => {
  try {
    // بناء عنوان URL مع معاملات البحث
    let url = '/api/inventory-transactions';
    const queryParams: string[] = [];
    
    if (params) {
      if (params.productId) queryParams.push(`productId=${params.productId}`);
      if (params.variantId) queryParams.push(`variantId=${params.variantId}`);
      if (params.warehouseId) queryParams.push(`warehouseId=${params.warehouseId}`);
      if (params.dateFrom) queryParams.push(`dateFrom=${params.dateFrom}`);
      if (params.dateTo) queryParams.push(`dateTo=${params.dateTo}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    const response = await axios.get(url, {
      headers: {
        'x-permission-context': 'inventory-movement'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory movements:', error);
    // إرجاع كائن مع علامة نجاح خاطئة في حالة الخطأ
    return {
      success: false,
      message: 'فشل في جلب بيانات حركة المخزون',
      movements: []
    };
  }
};